import { FREE_TRIAL_DAYS } from 'constants/billing';
import { PackageProviderName } from 'data/ppoints/constants';
import { useShowFreeTrial } from 'features/settings/components/ppoints/dashboard/dashboard-welcome/hooks/useShowFreeTrial';
import { UpsellKey } from 'features/google-tag-manager/enum/upsell-key';
import { useServiceProviderPlanLimit } from 'features/wizard/hooks/useServiceProviderPlanLimit';
import useCurrentSubscriptionHelper from 'hooks/billing/useCurrentSubscriptionHelper';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ServiceProviderPlanLimitBannerProps {
    /** Providers currently selected in the wizard step, not yet saved */
    selectedProviders: PackageProviderName[];
    /**
     * Discards the unsaved selection and hides the step's save bar. Awaited before the upgrade button navigates,
     * because the dirty save bar would otherwise block the top-level navigation to the pricing page.
     */
    onDiscardSelection: () => Promise<void>;
}

/**
 * Warns that the selection would activate more carriers than the current plan allows, before the merchant
 * tries to save it. Self-guards on that check, so it can be rendered unconditionally below the selector.
 */
export const ServiceProviderPlanLimitBanner: React.FC<ServiceProviderPlanLimitBannerProps> = ({
    selectedProviders,
    onDiscardSelection,
}) => {
    const { t } = useTranslation();
    const { goToPLans } = useCurrentSubscriptionHelper();
    const { limit, planName, isWithinLimit } = useServiceProviderPlanLimit();
    const showFreeTrial = useShowFreeTrial();

    // The over-limit selection can't be saved anyway, and the pricing page unloads the app, so nothing is lost.
    const handleUpgrade = async (): Promise<void> => {
        await onDiscardSelection();
        goToPLans(UpsellKey.WIZARD_SERVICE_PROVIDER_LIMIT);
    };

    if (isWithinLimit(selectedProviders)) {
        return null;
    }

    return (
        <s-banner tone="warning">
            <s-button slot="secondary-actions" onClick={handleUpgrade}>
                {showFreeTrial
                    ? t('WIZARD.SERVICE_PROVIDER_STEP.PLAN_LIMIT_BANNER.START_TRIAL', { count: FREE_TRIAL_DAYS })
                    : t('WIZARD.SERVICE_PROVIDER_STEP.PLAN_LIMIT_BANNER.UPGRADE_PLAN')}
            </s-button>
            {t('WIZARD.SERVICE_PROVIDER_STEP.PLAN_LIMIT_BANNER.DESCRIPTION', { count: limit, plan: planName })}
        </s-banner>
    );
};
